import { useRef, createRef } from 'react';

import Subheading from 'components/common/Subheading';
import Text from 'components/common/Text';
import ToggleButton from 'components/common/ToggleButton';
import SpoonAndFork from 'images/reviewCardGallery/SpoonAndFork.svg';
import { STANDARD_SERVINGS_PER_MEAL, HOME_CHEF_MEALS_PER_WEEK } from 'lib/constants';

import BestFitLabel from './BestFitLabel';

const containerText = {
  question: 'Number of Servings Per Recipe',
  subQuestion: 'Number of Recipes Per Week',
};

const StandardBoxCustomizer = ({
  servings,
  weeklyMeals,
  setServings,
  setWeeklyMeals,
  potentialSavingsText = '',
  boxRecommendations,
}) => {
  // For Keyboard nav accessibility
  const servingsRefs = useRef(STANDARD_SERVINGS_PER_MEAL.map(() => createRef()));
  const weeklyMealRefs = useRef(HOME_CHEF_MEALS_PER_WEEK.map(() => createRef()));

  return (
    <>
      <section>
        <Subheading className="mb-4 max-w-md text-left" brandStylesOverrides="text-secondary h5">
          {containerText.question}
        </Subheading>
        <div className="mb-8 flex flex-row text-center">
          <div
            className="grid w-full grid-cols-3 gap-x-2 gap-y-3"
            role="radiogroup"
            aria-label="Servings options"
          >
            {STANDARD_SERVINGS_PER_MEAL.map((numberOfServings, index) => (
              <ToggleButton
                id={numberOfServings}
                ref={servingsRefs.current[index]}
                selected={numberOfServings === servings}
                handleClick={() => setServings(numberOfServings)}
                className="relative w-full px-6 py-3"
                type="refreshToggle"
                key={numberOfServings}
                aria-label={`${numberOfServings} servings${numberOfServings === boxRecommendations.recommendedServings ? ', Best Fit' : ''}`}
                role="radio"
                aria-checked={numberOfServings === servings}
                tabIndex={numberOfServings === servings ? 0 : -1}
                refs={servingsRefs.current}
                setSelected={setServings}
              >
                {numberOfServings === boxRecommendations.recommendedServings && (
                  <BestFitLabel className="left-0 border-lemon-100 bg-lemon-70 bp1:left-4" />
                )}
                {numberOfServings}
              </ToggleButton>
            ))}
          </div>
        </div>
      </section>

      <section>
        <Subheading className="mb-4 max-w-md text-left" brandStylesOverrides="h5">
          {containerText.subQuestion}
        </Subheading>
        <div className="mb-8 flex flex-row text-center">
          <div
            className="grid w-full grid-cols-3 gap-x-2 gap-y-3"
            role="radiogroup"
            aria-label="Recipes options"
          >
            {HOME_CHEF_MEALS_PER_WEEK.map((numberOfWeeklyMeals, index) => (
              <ToggleButton
                id={numberOfWeeklyMeals}
                ref={weeklyMealRefs.current[index]}
                selected={numberOfWeeklyMeals === weeklyMeals}
                handleClick={() => setWeeklyMeals(numberOfWeeklyMeals)}
                className="relative w-full px-6 py-3"
                type="refreshToggle"
                key={numberOfWeeklyMeals}
                aria-label={`${numberOfWeeklyMeals} recipes${numberOfWeeklyMeals === boxRecommendations.recommendedMeals ? ', Best Fit' : ''}`}
                role="radio"
                aria-checked={numberOfWeeklyMeals === weeklyMeals}
                tabIndex={numberOfWeeklyMeals === weeklyMeals ? 0 : -1}
                refs={weeklyMealRefs.current}
                setSelected={setWeeklyMeals}
              >
                {numberOfWeeklyMeals === boxRecommendations.recommendedMeals && (
                  <BestFitLabel className="left-0 border-lemon-100 bg-lemon-70 bp1:left-4" />
                )}
                {numberOfWeeklyMeals}
              </ToggleButton>
            ))}
          </div>
        </div>
        {potentialSavingsText && (
          <div className="mb-8 flex flex-row items-center pl-2">
            <img src={SpoonAndFork} alt="SpoonAndFork" className="pr-4" />
            <Text brandStylesOverrides="text text-light" className="text-left">
              {potentialSavingsText}
            </Text>
          </div>
        )}
      </section>
    </>
  );
};

StandardBoxCustomizer.propTypes = {
  servings: PropTypes.number.isRequired,
  weeklyMeals: PropTypes.number.isRequired,
  setServings: PropTypes.func.isRequired,
  setWeeklyMeals: PropTypes.func.isRequired,
  potentialSavingsText: PropTypes.string,
  boxRecommendations: PropTypes.object.isRequired,
};

export default StandardBoxCustomizer;
